import { stripMacroStylingFromHtml } from "@redotech/redo-support-common/macro";

/**
 * Email-body HTML guards for the Plate composer.
 *
 * Kept free of any `platejs` runtime import so it stays unit-testable under Jest
 * (platejs is ESM and cannot be `require()`d). The actual `serializeHtml` call
 * lives in `use-message-editor`, which pipes its raw output through
 * {@link assertEmailSafeHtml} (a structural guarantee) and {@link sanitizeEmailHtml}.
 */

/**
 * Thrown when editor content fails to serialize into email-safe HTML — i.e.
 * Plate emitted raw Slate editor DOM (a `<div>` carrying the image URL in a data
 * attribute) instead of a real `<img>`. Surfacing this as an error rather than
 * silently sending it is what makes the "outgoing email drops/mangles inline
 * images" class of bug impossible to ship unnoticed: it fails the send loudly
 * and trips tests in CI.
 */
export class EmailHtmlSerializationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EmailHtmlSerializationError";
  }
}

// Plate's `stripDataAttributes` only removes data-slate-node/-type/-leaf/-string.
// Strip the rest of the editor-only bookkeeping (void wrappers, block ids, test
// ids) so the email body is clean semantic HTML rather than Slate editor DOM.
const SLATE_ONLY_ATTRS = / data-(?:slate-[a-z-]+|block-id|testid)="[^"]*"/g;
const SLATE_ONLY_CLASS = / class="slate-[^"]*"/g;
const POSITION_RELATIVE_DECLARATION =
  /(?:^|;)\s*position\s*:\s*relative\s*;?/gi;

export function sanitizeEmailHtml(html: string): string {
  return stripMacroStylingFromHtml(html)
    .replace(SLATE_ONLY_ATTRS, "")
    .replace(SLATE_ONLY_CLASS, "")
    .replace(/ style="([^"]*)"/g, (_, style: string) => {
      const sanitizedStyle = style
        .replace(POSITION_RELATIVE_DECLARATION, "")
        .replace(/^\s*;?\s*|\s*;?\s*$/g, "");
      return sanitizedStyle === "" ? "" : ` style="${sanitizedStyle}"`;
    });
}

// Plate serializes a soft line break (Shift+Enter, or a pasted multi-line
// string) as a literal `\n` inside a text node. The static serializer never
// emits newlines between tags, so every `\n` in its output is text content;
// left as-is HTML collapses it to a space and the sent email loses the break.
// Convert them to <br> so line breaks survive delivery (and htmlToText, which
// maps <br> back to `\n`, recovers the plain-text body correctly).
const TEXT_NEWLINE = /\r?\n/g;

export function convertTextNewlinesToBr(html: string): string {
  return html.replace(TEXT_NEWLINE, "<br>");
}

interface PlateNodeLike {
  type?: unknown;
  children?: unknown;
}

// Plate image element keys. A media node of one of these types must serialize
// to an `<img>`; if it doesn't, the recipient gets unrenderable Slate DOM.
const IMAGE_NODE_TYPES = new Set(["img", "image"]);

// Plate link element key. A link node of this type must serialize to an `<a>`
// with `href`; if it doesn't, the recipient sees plain text and the link is
// lost (PRO-1103).
const LINK_NODE_TYPES = new Set(["a"]);

// Plate's default block type and the trailing-block plugin both use `p`.
// Keep these helpers non-blocking: a serializer regression must not prevent a
// customer email from being sent.
const PARAGRAPH_NODE_TYPES = new Set(["p"]);

/** Count image nodes anywhere in a Plate value (depth-first). */
export function countImageNodes(value: unknown): number {
  if (!Array.isArray(value)) {
    return 0;
  }
  let count = 0;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  for (const node of value as PlateNodeLike[]) {
    if (node == null || typeof node !== "object") {
      continue;
    }
    if (typeof node.type === "string" && IMAGE_NODE_TYPES.has(node.type)) {
      count += 1;
    }
    count += countImageNodes(node.children);
  }
  return count;
}

/** Count link nodes anywhere in a Plate value (depth-first). */
export function countLinkNodes(value: unknown): number {
  if (!Array.isArray(value)) {
    return 0;
  }
  let count = 0;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  for (const node of value as PlateNodeLike[]) {
    if (node == null || typeof node !== "object") {
      continue;
    }
    if (typeof node.type === "string" && LINK_NODE_TYPES.has(node.type)) {
      count += 1;
    }
    count += countLinkNodes(node.children);
  }
  return count;
}

/** Count paragraph nodes anywhere in a Plate value (depth-first). */
export function countParagraphNodes(value: unknown): number {
  if (!Array.isArray(value)) {
    return 0;
  }
  let count = 0;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  for (const node of value as PlateNodeLike[]) {
    if (node == null || typeof node !== "object") {
      continue;
    }
    if (typeof node.type === "string" && PARAGRAPH_NODE_TYPES.has(node.type)) {
      count += 1;
    }
    count += countParagraphNodes(node.children);
  }
  return count;
}

/** Count `<img>` tags in a serialized HTML string. */
export function countImgTags(html: string): number {
  return (html.match(/<img[\s/>]/gi) ?? []).length;
}

/** Count `<a>` tags in a serialized HTML string. */
export function countAnchorTags(html: string): number {
  return (html.match(/<a[\s>]/gi) ?? []).length;
}

/** Count `<p>` tags in a serialized HTML string. */
export function countParagraphTags(html: string): number {
  return (html.match(/<p[\s>]/gi) ?? []).length;
}

/**
 * Guarantee every image and link node in the editor value made it into the
 * serialized HTML as a real `<img>` / `<a>`. Plate's static serializer silently
 * renders nodes that lack a static HTML serializer as a generic `<div>` (with
 * the URL stashed in a `data-slate-url` attribute), which no mail client
 * renders as a clickable link or visible image. Comparing node counts to tag
 * counts catches that semantically — independent of Plate's internal attribute
 * encoding — so a missing/regressed serializer fails loudly instead of shipping
 * a broken email.
 */
export function assertEmailSafeHtml(value: unknown, html: string): void {
  const imageNodes = countImageNodes(value);
  const imgTags = countImgTags(html);
  if (imgTags < imageNodes) {
    throw new EmailHtmlSerializationError(
      `Editor contains ${imageNodes} image node(s) but the serialized HTML has ` +
        `only ${imgTags} <img> tag(s): a media node did not serialize to an ` +
        "<img>. A node type is missing a static HTML serializer; sending this " +
        "would deliver raw editor DOM instead of the image.",
    );
  }
  const linkNodes = countLinkNodes(value);
  const anchorTags = countAnchorTags(html);
  if (anchorTags < linkNodes) {
    throw new EmailHtmlSerializationError(
      `Editor contains ${linkNodes} link node(s) but the serialized HTML has ` +
        `only ${anchorTags} <a> tag(s): a link node did not serialize to an ` +
        "<a>. A node type is missing a static HTML serializer; sending this " +
        "would drop the hyperlink and deliver plain text instead.",
    );
  }
}
